import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Header from './Header';
import { PixelWindow } from './PixelWindow';
import { Sidebar, SidebarItem } from './Sidebar';
import MusicPlayer from './MusicPlayer';
import RetroCalendar from './RetroCalendar';
import CatFollower from './CatFollower';
import { useNavigate } from 'react-router-dom';


// ----- helpers -----
const PUB = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
const resolvePublic = (p: string) =>
    /^https?:\/\//.test(p) ? p : `${PUB}${p.startsWith('/') ? '' : '/'}${p}`;

interface WinDef {
    id: number;
    title: string;
    defaultPosition: { x: number; y: number };
    content: React.ReactNode;
    style?: React.CSSProperties;
}

export default function Landing() {
    const [windows, setWindows] = useState<WinDef[]>([]);
    const [zMap, setZMap] = useState<Record<number, number>>({});
    const [topZ, setTopZ] = useState(1);
    const initializedRef = React.useRef(false);
    const navigate = useNavigate();

    const bringToFront = (id: number) => {
        setZMap(prev => ({ ...prev, [id]: topZ + 1 }));
        setTopZ(z => z + 1);
    };

    const closeWindow = (id: number) => {
        setWindows(ws => ws.filter(w => w.id !== id));
        setZMap(z => {
            const { [id]: _, ...rest } = z;
            return rest;
        });
    };

    const openWindow = (def: Omit<WinDef, 'id'>) => {
        setWindows(ws => {
            const existing = ws.find(w => w.title === def.title);
            if (existing) return ws;
            const newId = Math.max(0, ...ws.map(w => w.id)) + 1;
            return [...ws, { id: newId, ...def }];
        });
        // We need to bring to front, but we don't know the ID yet if it's new.
        // We can use a separate effect or just use a stable ID if title is unique.
    };

    // To handle bringToFront after opening, we can watch windows length
    useEffect(() => {
        if (windows.length > 0) {
            const lastWin = windows[windows.length - 1];
            bringToFront(lastWin.id);
        }
    }, [windows.length]);

    const aboutDef: Omit<WinDef, 'id'> = {
        title: 'About Me',
        defaultPosition: { x: 120, y: 60 },
        style: { width: '550px', height: 'auto' },
        content: (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gridTemplateRows: 'auto auto',
                    gap: '16px',
                    padding: '16px',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                }}
            >
                {/* Image with border */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', transform: 'scaleX(-1)' }}>
                    <img
                        src={resolvePublic('/potrait.webp')}  // check spelling on disk: "portrait.png" vs "potrait.png"
                        alt="Basieem"
                        style={{
                            width: '180px',
                            height: 'auto',
                            imageRendering: 'pixelated',
                            borderTop: '3px solid #00779d',
                            borderLeft: '3px solid #00779d',
                            borderBottom: '4px solid #00779d',
                            borderRight: '4px solid #00779d',
                        }}
                    />
                </div>

                {/* Yellow heading */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h2
                        style={{
                            color: '#e4ce01',
                            fontSize: '4rem',
                            margin: 0,
                            textAlign: 'left',
                        }}
                    >
                        Hey!<br />I'm Basieem
                    </h2>
                </div>

                {/* Full-width bottom text */}
                <div style={{ gridColumn: '1 / span 2', textAlign: 'left', color: '#54b990', fontSize: '1rem' }}>
                    <p>
                        I'm a Computer Science student at the University of Wisconsin–Madison. I love writing code that helps solve problems and makes my life easier.
                    </p>
                    <p>
                        Outside coding, I love camping, skiing, mountaineering, and indie video games, always seeking the next adventure both online and off.
                    </p>
                </div>
            </div>
        ),
    };

    const musicPlayer: Omit<WinDef, 'id'> = {
        title: 'Radio',
        defaultPosition: { x: 690, y: 60 },
        style: { width: '250px', height: 'auto' },
        content: <MusicPlayer channelName="Poolsuite FM (Default)" />
    };

    const calendarDef: Omit<WinDef, 'id'> = {
        title: 'Calendar',
        defaultPosition: { x: 960, y: 60 },
        style: { width: '280px', height: '280px' },
        content: <RetroCalendar />
    };

    const sidebarItems: SidebarItem[] = [
        { id: 'intro', label: 'Intro', iconSrc: '/launcher.png', onClick: () => openWindow(aboutDef) },
        { id: 'calendar', label: 'Calendar', iconSrc: '/calendar.png', onClick: () => openWindow(calendarDef) },
        { id: 'projects', label: 'Projects', iconSrc: '/projects_icon.png', onClick: () => navigate('/projects') },
        { id: 'resume', label: 'Résumé', iconSrc: '/resumepng.png', href: `${process.env.PUBLIC_URL}/resume.pdf` },
        { id: 'radio', label: 'Radio', iconSrc: '/radio.png', onClick: () => openWindow(musicPlayer) },
        { id: 'LinkedIn', label: 'LinkedIn', iconSrc: '/linkedIN.png', href: 'https://www.linkedin.com/in/basieem-s-ab3a8b149/' },
        { id: 'github', label: 'github', iconSrc: '/github.png', href: 'https://github.com/fabsway23' },
    ];

    useEffect(() => {
        if (!initializedRef.current) {
            openWindow(aboutDef);
            openWindow(musicPlayer);
            openWindow(calendarDef);
            initializedRef.current = true;
        }
    }, []);

    return (
        <Main>
            <Background />
            <Header />

            {windows.map(win => (
                <PixelWindow
                    key={win.id}
                    id={win.id}
                    title={win.title}
                    defaultPosition={win.defaultPosition}
                    zIndex={zMap[win.id] || 1}
                    onFocus={() => bringToFront(win.id)}
                    onClose={() => closeWindow(win.id)}
                    style={win.style}
                >
                    {win.content}
                </PixelWindow>
            ))}

            <Sidebar items={sidebarItems} />
            <Noise />
            <CRTOverlay />
           
        </Main >
    );
}

// styled
const CRTOverlay = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483646;
  background: repeating-linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.01) 0px,
    rgba(0, 0, 0, 0) 1px,
    transparent 2px
  );
  mix-blend-mode: overlay;
  opacity: 0.4;
`;

const Main = styled.div`
  width: 100vw;
  height: 100vh;
  position: relative;
`;

const Noise = styled.div`
  position: fixed; inset: 0; pointer-events: none;
  z-index: 2147483647;
  background-image: url(${resolvePublic('/noise.png')});
  background-repeat: repeat;
  background-size: 256px 256px;
  opacity: 0.02;
`;

const Background = styled.div`
  position: absolute;
  width: 100vw;
  height: 100vh;
  background-image: url(${resolvePublic('/star_bg.webp')});
  background-size: cover;
  z-index: -1;
  opacity: 1;
`;

